/**
 * `shell/AccountList.tsx` — the accounts signed in on this browser (#3005).
 *
 * One list, three placements:
 *   - `menu`  — the laptop account menu (`UserPill`), Radix menu rows;
 *   - `sheet` — the phone profile sheet (`ProfileSheet`), ruled rows;
 *   - `page`  — **Choose an account** (`/sign-in/choose`).
 *
 * Each row: the avatar (the Clerk photo when the person has one, else
 * initials), the name and the email. The current account carries a mark.
 * A pick calls `onPick`; the caller runs the switch. **Add another
 * account** closes the list and opens `/sign-in/add`.
 *
 * Page answers (web.md): the person holds two accounts and wants the
 * other one now; they see the accounts signed in here; they pick one or
 * add another.
 */
import { Check, Plus } from "lucide-react";
import { Link } from "react-router-dom";

import type { SignedInAccount } from "../../lib/accountSwitch";
import { ADD_ACCOUNT_PATH } from "../../lib/accountSwitch";
import { cn } from "../../lib/cn";
import { Avatar, DropdownMenuItem } from "../ui";

export const ADD_ACCOUNT_LABEL = "Add another account";

export type AccountListVariant = "menu" | "sheet" | "page";

export function AccountList({
  accounts,
  variant,
  onPick,
  onAdd,
}: {
  accounts: ReadonlyArray<SignedInAccount>;
  variant: AccountListVariant;
  onPick: (account: SignedInAccount) => void;
  /** Menu and sheet: close, then open the add page. The page uses a link. */
  onAdd?: () => void;
}) {
  return (
    <>
      {accounts.map((a) => (
        <AccountRow key={a.key} account={a} variant={variant} onPick={() => onPick(a)} />
      ))}
      <AddAccountRow variant={variant} onAdd={onAdd} />
    </>
  );
}

function AccountLines({ account }: { account: SignedInAccount }) {
  return (
    <span className="flex min-w-0 flex-1 flex-col text-left">
      <span className={cn("truncate text-sm text-fg", account.current && "font-medium")}>
        {account.name}
      </span>
      {account.email && account.email !== account.name ? (
        <span className="truncate text-xs text-fg-subtle">{account.email}</span>
      ) : null}
    </span>
  );
}

function CurrentMark({ account }: { account: SignedInAccount }) {
  return (
    <span className="flex w-4 shrink-0 justify-end">
      {account.current ? (
        <Check
          className="h-4 w-4 text-accent-fg"
          aria-label="Signed in now"
          data-testid={`account-${account.key}-current`}
        />
      ) : null}
    </span>
  );
}

function AccountRow({
  account,
  variant,
  onPick,
}: {
  account: SignedInAccount;
  variant: AccountListVariant;
  onPick: () => void;
}) {
  const body = (
    <>
      <Avatar name={account.name} imageUrl={account.imageUrl} size="md" decorative />
      <AccountLines account={account} />
      <CurrentMark account={account} />
    </>
  );
  const testid = `account-row-${account.key}`;
  if (variant === "menu") {
    return (
      <DropdownMenuItem onSelect={onPick} className="gap-2.5" data-testid={testid}>
        {body}
      </DropdownMenuItem>
    );
  }
  return (
    <button
      type="button"
      onClick={onPick}
      aria-current={account.current ? "true" : undefined}
      data-testid={testid}
      className={cn(
        "flex w-full items-center gap-3 text-left",
        variant === "sheet"
          ? "press-dim min-h-tap px-5 hover:bg-bg-subtle"
          : "min-h-[3.25rem] rounded-md border border-border px-3.5 py-2.5 hover:border-accent/40 hover:bg-bg-subtle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent",
      )}
    >
      {body}
    </button>
  );
}

function AddAccountRow({
  variant,
  onAdd,
}: {
  variant: AccountListVariant;
  onAdd: (() => void) | undefined;
}) {
  const body = (
    <>
      <span
        aria-hidden="true"
        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-dashed border-border text-fg-subtle"
      >
        <Plus className="h-3.5 w-3.5" />
      </span>
      <span className="flex-1 text-left text-sm text-fg">{ADD_ACCOUNT_LABEL}</span>
    </>
  );
  if (variant === "menu") {
    return (
      <DropdownMenuItem
        onSelect={() => onAdd?.()}
        className="gap-2.5"
        data-testid="account-add-another"
      >
        {body}
      </DropdownMenuItem>
    );
  }
  if (variant === "sheet") {
    return (
      <button
        type="button"
        onClick={() => onAdd?.()}
        data-testid="account-add-another"
        className="press-dim flex min-h-tap w-full items-center gap-3 px-5 hover:bg-bg-subtle"
      >
        {body}
      </button>
    );
  }
  return (
    <Link
      to={ADD_ACCOUNT_PATH}
      data-testid="account-add-another"
      className="flex min-h-[3.25rem] w-full items-center gap-3 rounded-md border border-dashed border-border px-3.5 py-2.5 hover:bg-bg-subtle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
    >
      {body}
    </Link>
  );
}
